import { useMemo } from 'react'
import { foldBundleEventBindings } from '@cogo/publisher-core/event-binding-shape'
import { useStudioStore } from '@/store/studio-store'
import { flowNamesMatch } from '@/features/flow-editor/services/flow-ownership'

export interface VariableEntity {
  id: string
  name: string
  blockName: string
  scope: string
  valueType: string
  defaultValue?: unknown
  persist?: boolean
  expression?: string
}

export interface DataSourceEntity {
  id: string
  name: string
  blockName: string
  scope: 'app' | 'block'
  sourceType: string
  config: Record<string, unknown>
}

export interface EventEntity {
  id: string
  name: string
  blockName: string
  eventKey: string
  componentId: string
  eventType: string
  boundFlow?: string
  params?: Record<string, unknown>
}

export interface FlowEntity {
  id: string
  name: string
  blockName: string
  trigger: string
  stepCount: number
}

export interface EntityIndex {
  variables: VariableEntity[]
  datasources: DataSourceEntity[]
  events: EventEntity[]
  flows: FlowEntity[]
  blocks: string[]
  summary: {
    variables: number
    datasources: number
    events: number
    flows: number
    blocks: string[]
  }
}

const EMPTY: EntityIndex = {
  variables: [],
  datasources: [],
  events: [],
  flows: [],
  blocks: [],
  summary: { variables: 0, datasources: 0, events: 0, flows: 0, blocks: [] },
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function unwrapFlowMap(value: unknown): Record<string, Record<string, unknown>> {
  if (!isRecord(value)) return {}
  const raw = isRecord(value.flows) ? value.flows : value
  return Object.fromEntries(
    Object.entries(raw).filter(([, flow]) => {
      if (!isRecord(flow)) return false
      return Array.isArray(flow.steps) || typeof flow.trigger === 'string'
    }),
  ) as Record<string, Record<string, unknown>>
}

export function useEntityIndex(): EntityIndex {
  const project = useStudioStore((s) => s.currentProject)

  return useMemo(() => {
    if (!project?.blocks) return EMPTY

    const variables: VariableEntity[] = []
    const datasources: DataSourceEntity[] = []
    const events: EventEntity[] = []
    const flows: FlowEntity[] = []
    const blockFlowNames: string[] = []
    const blocks = Object.keys(project.blocks)

    const manifestDatasources =
      project.manifest?.datasources as Record<string, Record<string, unknown>> | undefined
    if (manifestDatasources) {
      for (const [name, config] of Object.entries(manifestDatasources)) {
        const cfg = typeof config === 'object' && config !== null ? config : {}
        const { type, kind, ...rest } = cfg as Record<string, unknown>
        datasources.push({
          id: `app.datasources.${name}`,
          name,
          blockName: 'app',
          scope: 'app',
          sourceType: String(type ?? kind ?? 'static'),
          config: { ...(kind != null ? { kind } : {}), ...rest },
        })
      }
    }

    for (const [blockName, block] of Object.entries(project.blocks)) {
      if (!block) continue

      // Variables
      const vars = block.state as unknown as Record<string, Record<string, unknown>> | undefined
      if (vars) {
        for (const [scope, scopeVars] of Object.entries(vars)) {
          if (!scopeVars || typeof scopeVars !== 'object') continue
          for (const [name, def] of Object.entries(scopeVars as Record<string, unknown>)) {
            const d = typeof def === 'object' && def !== null ? (def as Record<string, unknown>) : {}
            variables.push({
              id: `${blockName}.variables.${name}`,
              name,
              blockName,
              scope,
              valueType: String(d.type ?? 'string'),
              defaultValue: d.default,
              persist: d.persist as boolean | undefined,
              expression: d.expression as string | undefined,
            })
          }
        }
      }

      // Datasources
      const ds = block.data as unknown as Record<string, Record<string, unknown>> | undefined
      if (ds) {
        for (const [name, config] of Object.entries(ds)) {
          const cfg = typeof config === 'object' && config !== null ? config : {}
          const { type, kind, ...rest } = cfg as Record<string, unknown>
          datasources.push({
            id: `${blockName}.datasources.${name}`,
            name,
            blockName,
            scope: 'block',
            sourceType: String(type ?? kind ?? 'rest'),
            config: { ...(kind != null ? { kind } : {}), ...rest },
          })
        }
      }

      // Events
      //
      // BOTH `bindings` and `elements` are canonical
      // (`@cogo/publisher-core/event-binding-shape`, this codebase's own
      // source of truth for the fact), and the agentic generator emits
      // `elements` 100% of the time — 357/357 committed events.json. Reading
      // one spelling alone is the failure that file records as turning 1,188
      // declared bindings into 0 handlers with 0 diagnostics; this reader was
      // taking `bindings` alone while six other Studio modules already folded
      // the two. Measured 2026-08-18 (slice 6): re-spelling one binding took
      // this table from 4 rows to 3 with the binding still declared.
      const ev = foldBundleEventBindings<Record<string, unknown>>(block.events)
      if (Object.keys(ev).length > 0) {
        for (const [eventKey, binding] of Object.entries(ev)) {
          const b = typeof binding === 'object' && binding !== null ? binding : {}
          const parts = eventKey.split(':')
          events.push({
            id: `${blockName}.events.${eventKey}`,
            name: eventKey,
            blockName,
            eventKey,
            componentId: parts[0] ?? eventKey,
            eventType: parts[1] ?? 'tap',
            boundFlow: b.flow as string | undefined,
            params: b.params as Record<string, unknown> | undefined,
          })
        }
      }

      // ActionFlows are canonically block-owned. Keep them visible in Data,
      // then suppress project-level mirrors of the same owned declaration.
      const blockFlows = unwrapFlowMap((block as { flow?: unknown }).flow)
      for (const [name, flow] of Object.entries(blockFlows)) {
        const steps = Array.isArray(flow.steps) ? flow.steps : []
        flows.push({
          id: `${blockName}.flows.${name}`,
          name,
          blockName,
          trigger: String(flow.trigger ?? 'event'),
          stepCount: steps.length,
        })
        blockFlowNames.push(name)
      }
    }

    // Flows (project-level)
    const projectFlows = project.flows as unknown as Record<string, Record<string, unknown>> | undefined
    if (projectFlows) {
      for (const [name, flow] of Object.entries(projectFlows)) {
        if (
          blockFlowNames.some(
            (blockFlowName) =>
              flowNamesMatch(blockFlowName, name) ||
              flowNamesMatch(name, blockFlowName),
          )
        ) continue
        const f = typeof flow === 'object' && flow !== null ? flow : {}
        const steps = Array.isArray(f.steps) ? f.steps : []
        flows.push({
          id: `global.flows.${name}`,
          name,
          blockName: 'global',
          trigger: String(f.trigger ?? 'event'),
          stepCount: steps.length,
        })
      }
    }

    return {
      variables,
      datasources,
      events,
      flows,
      blocks,
      summary: {
        variables: variables.length,
        datasources: datasources.length,
        events: events.length,
        flows: flows.length,
        blocks,
      },
    }
  }, [project])
}
